// In-browser proof-of-possession for the dashboard's "generate & enroll" flow
// (v0.6 vouching). Reuses the existing canonical PEM + fingerprint helpers
// (pem.ts) so the only new surface here is the registration challenge and its
// signature. The encodings must byte-match the server; a Node-run parity test
// (tests/registration-crypto-parity.test.ts) pins the equivalence against the
// real lib functions (ed25519KeyFingerprint, registrationChallengeMessage).
//
//   challenge = registrationChallengeMessage(...)        (src/lib/messages.ts)
//             = lengthPrefixedUtf8(["SEQUESIGN_REGISTRATION_CHALLENGE_V1",
//                 role, partyType ?? "", identity, fingerprint])
//   subject_signature = base64(Ed25519 sign(challenge, secretKey))

import { ed25519 } from "@noble/curves/ed25519.js";
import { encodeEd25519SpkiPem, encodeEd25519Pkcs8Pem, fingerprint } from "./pem";

export type EnrollmentMaterial = {
  // Canonical PEMs (byte-identical to Node's export); the private key is shown
  // once for the subject to download and never reaches Sequesign.
  publicKeyPem: string;
  privateKeyPem: string;
  subjectKeyFingerprint: string;
  // base64 Ed25519 signature over the registration challenge.
  subjectSignature: string;
};

export type EnrollmentRequest =
  | { role: "approver"; partyType: "human" | "agent"; identity: string }
  | { role: "counterparty"; identity: string };

function base64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

// Mirrors lengthPrefixedUtf8 (src/lib/encoding.ts): each field is a 4-byte
// big-endian length followed by its UTF-8 bytes, concatenated.
function lengthPrefixedUtf8(fields: string[]): Uint8Array {
  const enc = new TextEncoder();
  const parts: Uint8Array[] = [];
  for (const field of fields) {
    const bytes = enc.encode(field);
    const len = new Uint8Array(4);
    new DataView(len.buffer).setUint32(0, bytes.length, false);
    parts.push(len, bytes);
  }
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

// Generate a fresh Ed25519 keypair and produce everything POST /registrations
// needs, including the private key PEM to hand to the subject ONCE.
export async function buildEnrollmentMaterial(req: EnrollmentRequest): Promise<EnrollmentMaterial> {
  const secretKey = ed25519.utils.randomSecretKey();
  const publicKey = ed25519.getPublicKey(secretKey);

  const publicKeyPem = encodeEd25519SpkiPem(publicKey);
  const privateKeyPem = encodeEd25519Pkcs8Pem(secretKey);
  const subjectKeyFingerprint = await fingerprint(publicKeyPem);

  const partyType = req.role === "approver" ? req.partyType : "";
  const challenge = lengthPrefixedUtf8([
    "SEQUESIGN_REGISTRATION_CHALLENGE_V1",
    req.role,
    partyType,
    req.identity,
    subjectKeyFingerprint
  ]);
  const subjectSignature = base64(ed25519.sign(challenge, secretKey));

  return { publicKeyPem, privateKeyPem, subjectKeyFingerprint, subjectSignature };
}
